import { Box } from 'src/design/atoms/Box';
import { Divider } from 'src/design/atoms/Divider';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { SimpleGrid } from 'src/design/atoms/SimpleGrid';
import { Stack } from 'src/design/atoms/Stack';
import { Checkbox } from 'src/design/atoms/Checkbox';
import { Input } from 'src/design/atoms/Input';
import { Select } from 'src/design/atoms/Select';
import React from 'react';
import {
  JobManhoursFilterDto,
  ListJobManhoursRequest,
} from 'src/api/FiretrolApiClient';
import { useManhoursFilters } from 'src/api/queries/manhours/useManhoursFilters';
import { useManhoursSummary } from 'src/api/queries/manhours/useManhoursSummary';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { Header } from 'src/design/organisms/Header';
import { BaseCostCodeSelector } from './components/BaseCostCodeSelector';
import { CostCodeSelector } from './components/CostCodeSelector';
import { DivisionSelector } from './components/DivisionSelector';
import { ManhoursChart } from './components/ManhoursChart';
import { ManhoursTable } from './components/ManhoursTable';
import { useManhoursData } from './hooks';

const weekOptions = Array.from({ length: 20 }, (_, i) => ({
  value: (i + 1).toString(),
  label: `${i + 1} Week${i > 0 ? 's' : ''}`,
}));

const EMPTY_FILTERS: JobManhoursFilterDto[] = [];

type AppliedFilters = Pick<
  ListJobManhoursRequest,
  'divisions' | 'costCodes' | 'showClosed'
>;

export const Manhours: React.FC = () => {
  const [selectedDivisions, setSelectedDivisions] = React.useState<string[]>(
    [],
  );
  const [selectedBaseCostCodes, setSelectedBaseCostCodes] = React.useState<
    string[]
  >([]);
  const [selectedCostCodes, setSelectedCostCodes] = React.useState<string[]>(
    [],
  );
  const [chart, setChart] = React.useState(true);
  const [showClosed, setShowClosed] = React.useState(false);
  const [ldOnly, setLdOnly] = React.useState(false);
  const [weekCount, setWeekCount] = React.useState(13);

  // Divisions, cost codes, and closed jobs reach the report only on Apply, so
  // the query takes this snapshot rather than the live selections. With no
  // divisions the server returns the weeks without rows.
  const [appliedFilters, setAppliedFilters] = React.useState<AppliedFilters>({
    divisions: [],
    costCodes: [],
    showClosed: false,
  });

  const { data: filterData = EMPTY_FILTERS } = useManhoursFilters();
  const { data: manhoursResponse, isFetching: loading } = useManhoursSummary({
    ...appliedFilters,
    ldOnly,
    weekCount,
  });

  const { datePeriods, summary, chartData, rows, totalCtcHours } =
    useManhoursData(manhoursResponse);

  // Get available divisions from filter data
  const availableDivisions = React.useMemo(
    () => [
      ...new Set(
        filterData
          .map((i) => i.division)
          .filter((i) => i !== null)
          .sort(),
      ),
    ],
    [filterData],
  );

  // Get available base cost codes based on selected divisions
  const availableBaseCostCodes = React.useMemo(
    () => [
      ...new Set(
        filterData
          .filter((item) => selectedDivisions.includes(item.division))
          .map((i) => i.baseCostCode)
          .filter((i) => i !== null)
          .sort(),
      ),
    ],
    [selectedDivisions, filterData],
  );

  // Get available cost codes based on selected divisions and base cost codes
  const availableCostCodes = React.useMemo(
    () => [
      ...new Set(
        filterData
          .filter((item) => selectedDivisions.includes(item.division))
          .filter((item) => selectedBaseCostCodes.includes(item.baseCostCode))
          .map((i) => i.costCode)
          .filter((i) => i !== null)
          .sort(),
      ),
    ],
    [selectedDivisions, selectedBaseCostCodes, filterData],
  );

  const clearFilters = () => {
    setSelectedDivisions([]);
    setSelectedBaseCostCodes([]);
    setSelectedCostCodes([]);
    setShowClosed(false);
    setLdOnly(false);
  };

  const applyFilters = () => {
    setAppliedFilters({
      divisions: selectedDivisions,
      costCodes: selectedCostCodes,
      showClosed,
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChart(event.target.checked);
  };

  return (
    <>
      <Header
        title="Manhours"
        helpText="View manhour analytics for your organization. See project manhours by cost code and date periods."
        breadcrumbProps={{
          items: [{ text: 'Manhours' }],
        }}
      />

      <Stack gap="md">
        <SimpleGrid cols={3}>
          <DivisionSelector
            onChange={(v) => setSelectedDivisions(v)}
            value={selectedDivisions}
            options={availableDivisions}
          />

          <BaseCostCodeSelector
            onChange={(v) => setSelectedBaseCostCodes(v)}
            value={selectedBaseCostCodes}
            options={availableBaseCostCodes}
            disabled={selectedDivisions.length === 0}
          />

          <CostCodeSelector
            onChange={(o) => setSelectedCostCodes(o)}
            value={selectedCostCodes}
            options={availableCostCodes}
            disabled={
              selectedDivisions.length === 0 ||
              selectedBaseCostCodes.length === 0
            }
          />
        </SimpleGrid>

        <Checkbox
          label="Show Closed Jobs"
          checked={showClosed}
          onChange={(event) => setShowClosed(event.currentTarget.checked)}
        />

        <Group justify="flex-end">
          <BasicButton
            variant="subtle"
            onClick={clearFilters}
            disabled={loading}
          >
            Clear Filters
          </BasicButton>
          <BasicButton
            onClick={applyFilters}
            disabled={loading}
            loading={loading}
          >
            Apply Filters
          </BasicButton>
        </Group>

        <Divider />

        <Input.Wrapper label="Week Count">
          <Select
            value={weekCount.toString()}
            onChange={(value) => setWeekCount(Number(value))}
            data={weekOptions}
          />
        </Input.Wrapper>

        <Group gap="xl">
          <Checkbox
            label="Show Chart"
            checked={chart}
            onChange={handleChange}
          />
          <Checkbox
            label="LD jobs only"
            checked={ldOnly}
            onChange={(event) => setLdOnly(event.currentTarget.checked)}
          />
        </Group>

        <Box>
          {loading ? (
            <Group justify="center" h={200}>
              <Loader />
            </Group>
          ) : (
            <>
              {chart && <ManhoursChart data={chartData} />}
              <ManhoursTable
                datePeriods={datePeriods}
                summary={summary}
                rows={rows}
                totalCtcHours={totalCtcHours}
              />
            </>
          )}
        </Box>
      </Stack>
    </>
  );
};
